import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { AppointmentFormBody } from "@/components/calendar/AppointmentForm";
import type { AppointmentTypeBehavior } from "@/hooks/useClinicAppointmentTypes";

interface MobileAppointmentCreateDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  prefilledDate?: Date;
  prefilledTime?: string;
  prefilledEndTime?: string;
  prefilledOwnerId?: string;
  prefilledPetId?: string;
  prefilledPetIds?: string[];
  prefilledVetId?: string;
  prefilledType?: string;
  prefilledBehavior?: AppointmentTypeBehavior;
  onSuccess?: () => void;
}

export function MobileAppointmentCreateDrawer({
  open,
  onOpenChange,
  onSuccess,
  ...formProps
}: MobileAppointmentCreateDrawerProps) {
  const { t } = useTranslation(["calendar", "common"]);

  const handleClose = () => onOpenChange(false);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={cn(
          "h-[100dvh] max-h-[100dvh] rounded-none border-0 bg-background-app p-0",
          "flex flex-col focus:outline-none",
        )}
      >
        <header className="shrink-0 border-b border-border/40 bg-background-app/90 px-4 pb-3 pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              aria-label={t("common:close")}
              onClick={handleClose}
              className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/40 bg-card shadow-sm"
            >
              <X className="size-[18px] text-foreground" />
            </button>
            <div className="text-[19px] font-extrabold leading-tight tracking-tight">
              {t("calendar:appointmentForm.createTitle")}
            </div>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col bg-background-app">
          {open && (
            <AppointmentFormBody
              mode="create"
              mobileLayout
              onClose={handleClose}
              onSuccess={() => {
                onSuccess?.();
                handleClose();
              }}
              {...formProps}
            />
          )}
        </main>
      </DrawerContent>
    </Drawer>
  );
}
